import { z } from "zod";
import { customerItineraryOutcomeSchema, itineraryEditModeValues, itineraryEditOperationSchema, itineraryFeasibilityValues, itineraryIssueSchema, itineraryItemChangesShape, itineraryItemEntryShape, itineraryItemKindValues, itineraryItemStatusValues, itineraryPlanSectionSchema, itineraryReplaceTargetSchema, itinerarySectionKindValues, itineraryStatusValues, itineraryTimeZoneSourceValues, itineraryTimingKindValues, itineraryUuid, MAX_ITINERARY_OPERATIONS, MAX_ITINERARY_SECTIONS, weatherSensitivityValues, } from "./itinerary-edit.js";
import { staffItineraryValidationRunSchema } from "./itinerary-validation-run.js";
import { staffRequestStateValues, staffRequestStatusCodeValues } from "./staff-requests.js";
// The staff itinerary contract of Milestone D (CHT-742; docs/architecture/milestone-d-contracts.md sections 3.3, 3.6 and 8): the
// routes MD-I7 to MD-I12. Staff see everything (lineage ids, staff notes, provenance, creators, the validation report, the revision
// list, the change audit); a staff command takes wall times like a customer one and never a UTC instant. Staff-only fields appear
// here and in no customer shape (`itinerary-edit.ts`): the two files are separate on purpose.
const instant = z.iso.datetime({ offset: false });
const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);
const timeZone = z.string().regex(/^(UTC|[A-Za-z_]+(\/[A-Za-z0-9_+-]+){1,2})$/u);
const version = z.number().int().min(1).max(2_147_483_647);
const text = (max) => z.string().max(max);
// --- inputs ---------------------------------------------------------------------------------------------------------------
/** What staff may send for an item: the customer fields plus a staff note and coordinates (Lafayette-owned geography). */
export const staffItineraryItemEntrySchema = z.strictObject({
    ...itineraryItemEntryShape,
    staffNote: text(1000).nullish(),
    latitude: z.number().min(-90).max(90).nullish(),
    longitude: z.number().min(-180).max(180).nullish(),
});
export const staffItineraryItemChangesSchema = z.strictObject({
    ...itineraryItemChangesShape,
    staffNote: text(1000).nullable().optional(),
    latitude: z.number().min(-90).max(90).nullable().optional(),
    longitude: z.number().min(-180).max(180).nullable().optional(),
});
export const staffItineraryEditOperationSchema = itineraryEditOperationSchema(staffItineraryItemEntrySchema, staffItineraryItemChangesSchema);
const reason = text(500).nullish();
/** `POST /staff/itineraries` (MD-I9): a STAFF draft for a customer's trip, with content, hidden from the customer until staff activate it. */
export const staffItineraryCreateBodySchema = z.strictObject({
    tripId: itineraryUuid,
    title: text(140).min(1),
    sections: z
        .array(itineraryPlanSectionSchema(staffItineraryItemEntrySchema))
        .max(MAX_ITINERARY_SECTIONS),
    replace: itineraryReplaceTargetSchema.nullish(),
    reason,
});
/** `POST /staff/itineraries/:itineraryId/operations` (MD-I10): operations on a DRAFT, in place, against the version staff read. */
export const staffItineraryOperationsBodySchema = z.strictObject({
    expectedVersion: version,
    operations: z.array(staffItineraryEditOperationSchema).min(1).max(MAX_ITINERARY_OPERATIONS),
    reason,
});
/** `POST /staff/trips/:tripId/itinerary/edits` (MD-I11): apply or propose against the plan of record; a consequential apply needs a reason. */
export const staffItineraryEditBodySchema = z.strictObject({
    mode: z.enum(itineraryEditModeValues),
    baseItineraryId: itineraryUuid,
    expectedRevision: version,
    operations: z.array(staffItineraryEditOperationSchema).min(1).max(MAX_ITINERARY_OPERATIONS),
    reason,
});
/**
 * `POST /staff/itineraries/:itineraryId/activate` (MD-I12). Staff may acknowledge only `TRAVEL_TIME_EXCEEDS_GAP`, with a reason of
 * at least 10 characters; the acknowledgement is audited and the plan is shown as infeasible.
 */
export const staffItineraryActivateBodySchema = z.strictObject({
    expectedVersion: version,
    reason,
    acknowledgeIssues: z.array(z.literal("TRAVEL_TIME_EXCEEDS_GAP")).max(1).optional(),
});
export const staffItineraryArchiveBodySchema = z.strictObject({ expectedVersion: version, reason });
/**
 * `POST /staff/itineraries/:itineraryId/validate` (MD-I12). With no body it runs what the server composes (the full run when the route and
 * weather checks are composed, else the structural check). `mode: "FULL"` asks for the composed run (the answer carries `run`; a server
 * with no evidence services answers the structural check and no `run`). `mode: "STRUCTURAL"` runs the structure and the times only.
 * `expectedVersion` is the version staff are looking at: a plan that moved on answers `itinerary.version_conflict`.
 */
export const staffItineraryValidateModeValues = ["FULL", "STRUCTURAL"];
export const staffItineraryValidateBodySchema = z.strictObject({
    mode: z.enum(staffItineraryValidateModeValues).optional(),
    expectedVersion: version.optional(),
});
// --- outputs --------------------------------------------------------------------------------------------------------------
/** The outcome of a staff command: the customer outcome plus the id of the audit row and the warning codes. */
export const staffItineraryOutcomeSchema = customerItineraryOutcomeSchema.extend({
    changeId: itineraryUuid.nullable(),
    warningCodes: z.array(z.string().min(1).max(64)).max(64),
});
const actorKind = z.enum(["customer", "staff", "system"]);
/** One itinerary of a trip (the revision list). `feasibility` is never read from here: it is the newest validation report. */
export const staffItineraryRowSchema = z.strictObject({
    id: itineraryUuid,
    tripId: itineraryUuid,
    customerId: itineraryUuid,
    status: z.enum(itineraryStatusValues),
    revision: version,
    version,
    supersedesItineraryId: itineraryUuid.nullable(),
    title: z.string(),
    createdBy: z.enum(["CUSTOMER", "STAFF"]),
    createdChannel: z.string().min(1).max(64),
    createdByStaffUserId: itineraryUuid.nullable(),
    activatedAt: instant.nullable(),
    supersededAt: instant.nullable(),
    archivedAt: instant.nullable(),
    statusChangedAt: instant,
    createdAt: instant,
    updatedAt: instant,
});
export const staffItineraryRevisionsSchema = z.strictObject({
    items: z.array(staffItineraryRowSchema).max(100),
});
export const staffItinerarySectionSchema = z.strictObject({
    id: itineraryUuid,
    itineraryId: itineraryUuid,
    position: z.number().int().min(0),
    kind: z.enum(itinerarySectionKindValues),
    localDate: localDate.nullable(),
    timeZone: timeZone.nullable(),
    title: z.string().nullable(),
});
/**
 * CHT-747 (part 2), ADD-ONLY: what staff read about one item beside its columns. `view` is separate from the columns so that no stored
 * field changes meaning (`endTimeZone` of the item is the stored end zone; `view.endTimeZone` is the zone `localEnd` is read in).
 * It holds states, observation times, closed codes and ids: no provider name, no payload, no reason code, no staff note, no title.
 */
const itemEvidenceStateValues = ["VERIFIED", "UNVERIFIED", "STALE"];
export const staffItemViewSchema = z.strictObject({
    /** `YYYY-MM-DDTHH:MM`, computed on the server from the stored instant and the item zone (the customer view's own helper). */
    localStart: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/u)
        .nullable(),
    localEnd: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/u)
        .nullable(),
    endTimeZone: timeZone.nullable(),
    /** The newest STORED readings of the item. No provider is asked by a read. */
    evidence: z.strictObject({
        route: z.strictObject({
            state: z.enum(itemEvidenceStateValues),
            observedAt: instant.nullable(),
            /** How the stored travel time was made. `STATIC_HEURISTIC` is never `VERIFIED`. */
            basis: z.enum(["TRAFFIC_UNAWARE", "TRAFFIC_AWARE", "STATIC_HEURISTIC"]).nullable(),
        }),
        weather: z.strictObject({
            state: z.enum(itemEvidenceStateValues),
            observedAt: instant.nullable(),
            level: z.enum(["NONE", "WATCH", "HIGH", "SEVERE"]).nullable(),
            /** `LOCATION_CHANGED`: the newest reading of this lineage is about another place than the item has now, so it is not used. */
            reason: z.enum(["LOCATION_CHANGED"]).nullable().optional(),
        }),
    }),
    /** The most recent requests linked to the item by lineage, most recent first, closed ones included. State is live. */
    requests: z
        .array(z.strictObject({
        requestId: itineraryUuid,
        state: z.enum(staffRequestStateValues),
        statusCode: z.enum(staffRequestStatusCodeValues),
        terminal: z.boolean(),
        via: z.enum(["RAISED_ABOUT", "TRACKED_BY_ITEM"]),
        /** A payment quote or payment request exists for the request. It is not a payment state. */
        hasPayment: z.boolean(),
    }))
        .max(20),
    /** All requests linked to the item, and whether `requests` is cut. Optional for a client that reads an older server. */
    requestsTotal: z.number().int().min(0).optional(),
    requestsTruncated: z.boolean().optional(),
    /** `null` for an item that links no catalog item. */
    catalogState: z.enum(["PUBLISHED", "UNPUBLISHED", "MISSING"]).nullable(),
});
/** Every column of an item, the staff-only ones included. */
export const staffItineraryItemSchema = z.strictObject({
    id: itineraryUuid,
    itineraryId: itineraryUuid,
    sectionId: itineraryUuid,
    position: z.number().int().min(0),
    lineageId: itineraryUuid,
    kind: z.enum(itineraryItemKindValues),
    itemStatus: z.enum(itineraryItemStatusValues),
    title: z.string(),
    timingKind: z.enum(itineraryTimingKindValues),
    startsAt: instant.nullable(),
    endsAt: instant.nullable(),
    timeZone: timeZone.nullable(),
    endTimeZone: timeZone.nullable(),
    durationMinutes: z.number().int().nullable(),
    enteredLocalStart: z.string().nullable(),
    enteredUtcOffsetMinutes: z.number().int().nullable(),
    enteredLocalEnd: z.string().nullable(),
    enteredEndUtcOffsetMinutes: z.number().int().nullable(),
    timeZoneSource: z.enum(itineraryTimeZoneSourceValues).nullable(),
    timeZoneObservedAt: instant.nullable(),
    isFixed: z.boolean(),
    weatherSensitivity: z.enum(weatherSensitivityValues),
    locationName: z.string().nullable(),
    locality: z.string().nullable(),
    countryCode: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    catalogItemId: itineraryUuid.nullable(),
    externalPlace: z
        .strictObject({
        provider: z.string(),
        id: z.string(),
        name: z.string(),
        observedAt: instant,
    })
        .nullable(),
    conciergeRequestId: itineraryUuid.nullable(),
    customerNote: z.string().nullable(),
    staffNote: z.string().nullable(),
    provenance: z.string().min(1).max(64),
    createdByActorKind: actorKind,
    createdByActorId: z.string().min(1).max(128),
    originItemId: itineraryUuid.nullable(),
    /**
     * CHT-747: the read model of the item (see `staffItemViewSchema`). The server always sends it. It is optional here so a client that is
     * newer than the API instance it talks to (a deploy in progress) still reads the plan; the page then says the details are not
     * available from that server version.
     */
    view: staffItemViewSchema.optional(),
});
const actorSchema = z.strictObject({ kind: actorKind, id: z.string().min(1).max(128) });
/** The newest validation report of an itinerary: codes and counts, and which codes staff acknowledged (never the reason). */
export const staffItineraryReportSchema = z.strictObject({
    id: itineraryUuid,
    itineraryId: itineraryUuid,
    itineraryVersion: version,
    feasibility: z.enum(itineraryFeasibilityValues),
    source: z.enum(["EDIT_APPLY", "EDIT_PROPOSE", "ACTIVATION", "VALIDATION_RUN"]),
    issueCodes: z
        .array(z.strictObject({
        code: z.string().min(1).max(64),
        severity: z.enum(["BLOCKING", "WARNING"]),
        count: z.number().int().min(1),
        /**
         * The items (stable ids) whose gap staff acknowledged, on the entry of the one code staff may acknowledge. Only the issues that
         * really carry the acknowledgement are listed (CHT-747 review F2).
         */
        acknowledgedStableIds: z.array(itineraryUuid).max(12).optional(),
    }))
        .max(80),
    blockingCount: z.number().int().min(0),
    warningCount: z.number().int().min(0),
    acknowledgedCodes: z.array(z.string().min(1).max(64)).max(8),
    checkedAt: instant,
    actor: actorSchema,
});
/** One audited edit: ids, kinds and field names only (no value, no text). */
export const staffItineraryChangeSchema = z.strictObject({
    id: itineraryUuid,
    tripId: itineraryUuid,
    itineraryId: itineraryUuid,
    baseItineraryId: itineraryUuid.nullable(),
    command: z.enum([
        "CREATE_PLAN",
        "REPLACE_PLAN",
        "EDIT_DRAFT",
        "PROPOSE_EDIT",
        "APPLY_EDIT",
        "ACTIVATE",
    ]),
    actor: actorSchema,
    channel: z.string().min(1).max(64),
    operations: z.record(z.string(), z.unknown()),
    approval: z.enum(["NONE", "CUSTOMER_CONFIRMED", "STAFF"]),
    createdAt: instant,
});
/** `GET /staff/itineraries/:itineraryId` (MD-I8). */
export const staffItineraryDetailSchema = z.strictObject({
    itinerary: staffItineraryRowSchema,
    sections: z.array(staffItinerarySectionSchema).max(366),
    items: z.array(staffItineraryItemSchema).max(1_000),
    issues: z.array(itineraryIssueSchema).max(1_000),
    report: staffItineraryReportSchema.nullable(),
    revisions: z.array(staffItineraryRowSchema).max(100),
    changes: z.array(staffItineraryChangeSchema).max(100),
});
/** `POST /staff/itineraries/:itineraryId/validate` (MD-I12): the structural and temporal issues and the report row. */
export const staffItineraryValidationSchema = z.strictObject({
    itineraryId: itineraryUuid,
    itineraryVersion: version,
    feasibility: z.enum(itineraryFeasibilityValues),
    issues: z.array(itineraryIssueSchema).max(1_000),
    report: staffItineraryReportSchema,
    /** CHT-746: the route and weather evidence of the run. Absent when the server runs no evidence checks (providers not composed). */
    run: staffItineraryValidationRunSchema.optional(),
});
